import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { createSelector } from "reselect";
import Snackbars from "components/Snackbar/Snackbar.js";
import PriceList from "components/PriceList";
import SearchMenu from "components/SearchMenuConfigSync/SearchMenuConfigSync.jsx";
import ModalPrice from "components/ModalPrice/ModalPrice";
import { PageContainer } from "./styles";
import { useColorTheme } from "hooks/useColorTheme";
import { useTimer } from "hooks/useTimer";
import {
  getData,
  getPrices,
  sendNewPrice,
  changeCompany,
  changeStation,
  clearError,
} from "redux/ducks/updatePrice";

const companiesMapSelector = createSelector(
  (state) => state.updatePrice.companies,
  (companies) =>
    companies.reduce((acum, company) => {
      acum.set(company.id, company);
      return acum;
    }, new Map())
);

const mapStations = () =>
  createSelector(
    (state) => state.updatePrice.stations,
    (stations) =>
      stations.reduce((acum, station) => {
        acum.set(station.id, station.name);
        return acum;
      }, new Map())
  );

export default function UpdatePricePage() {
  const dispatch = useDispatch();
  const [runTimer] = useTimer(1000);
  const [modal, setModal] = React.useState(false);
  const [itemModal, setItemModal] = React.useState(null);
  const [priceModal, setPriceModal] = React.useState("");
  // selectors
  const { error, errorMessage } = useSelector(
    (state) => state.updatePrice,
    shallowEqual
  );
  const prices = useSelector((state) => state.updatePrice.prices);
  const companies = useSelector(
    (state) => state.updatePrice.companies,
    shallowEqual
  );
  const stations = useSelector((state) => state.updatePrice.companyStations);
  const companiesMap = useSelector(companiesMapSelector);
  const companySelected = useSelector(
    (state) => state.updatePrice.companySelected
  );
  const stationSelected = useSelector(
    (state) => state.updatePrice.stationSelected
  );

  const colorBg = useSelector((state) => state.config.colorBg);
  const mapStaionsMemoized = useMemo(mapStations, [stations]);
  const stationsMap = useSelector((state) => mapStaionsMemoized(state));

  const getStationName = (stationsMap, stationSelected) => {
    const name = stationsMap.get(stationSelected);
    return name ? name : "";
  };

  const stationsName = useMemo(
    () => getStationName(stationsMap, stationSelected),
    [stationsMap, stationSelected]
  );
  // Custom hooks
  const { color, colorRGB } = useColorTheme(colorBg);

  // calls
  const fetchCompanies = useCallback(() => {
    dispatch(getData());
  }, [dispatch]);

  const fetchPrices = useCallback(
    (id) => {
      dispatch(getPrices(id));
    },
    [dispatch]
  );

  const changeCompanyMemoized = useCallback(
    (company, stations) => {
      dispatch(changeCompany(company, stations));
    },
    [dispatch]
  );
  const changeStationMemoized = useCallback(
    (station) => {
      dispatch(changeStation(station));
    },
    [dispatch]
  );
  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, []);

  const handleCompanySelect = (event) => {
    const actualCompany = companiesMap.get(event.target.value);
    const newStations = actualCompany
      ? actualCompany.stations.map((company) => {
          return { id: company.id, name: company.name };
        })
      : [];
    changeCompanyMemoized(event.target.value, newStations);
  };

  const handleStationSelect = (event) => {
    changeStationMemoized(event.target.value);
    if (event.target.value) {
      runTimer(fetchPrices, [event.target.value]);
    }
  };

  const handleOpenModal = (product) => {
    setItemModal(product);
    setPriceModal(product.price);
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
  };

  const handleSendPrice = (product, price) => {
    if (product && price) {
      const body = { price: Number(price) };
      dispatch(sendNewPrice(product.id, stationSelected, body));
      setModal(false);
    }
  };

  const handleChangePrice = (event) => {
    setPriceModal(event.target.value);
  };

  // Effects
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  return (
    <PageContainer>
      <SearchMenu
        color={color}
        colorRGB={colorRGB}
        companies={companies}
        stations={stations}
        companySelect={companySelected}
        stationSelect={stationSelected}
        onCompanySelect={handleCompanySelect}
        onStationSelect={handleStationSelect}
      />
      <PriceList
        prices={prices}
        stationName={stationsName}
        color={color}
        colorRGB={colorRGB}
        onOpenModal={handleOpenModal}
        onCloseModal={handleCloseModal}
      />
      <ModalPrice
        color={color}
        colorRGB={colorRGB}
        onCloseModal={handleCloseModal}
        isOpen={modal}
        item={itemModal}
        price={priceModal}
        onChangePrice={handleChangePrice}
        onSubmit={handleSendPrice}
      />
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={clearSnackbar}
        close
      />
    </PageContainer>
  );
}
